import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "AED", symbol: "AED" },
  { code: "SGD", symbol: "S$" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
];

const COMMON_SKILLS = [
  "Selenium", "Playwright", "Cypress", "API Testing", "Postman", "REST Assured",
  "JMeter", "K6", "TestNG", "JUnit", "Pytest", "Appium",
  "Java", "Python", "JavaScript", "Manual Testing", "CI/CD", "Jenkins",
];

const toNonNegInt = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const cleaned = String(raw).replace(/[^\d]/g, "");
  if (cleaned === "") return "";
  return String(parseInt(cleaned, 10));
};

/**
 * Compact "Edit Job" dialog — restricted to the 7-day post window.
 * Reuses the same schema as PostJob but rendered inside a scrollable modal.
 */
export default function EditJobDialog({ open, onOpenChange, job, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [reqInput, setReqInput] = useState("");
  const [niceInput, setNiceInput] = useState("");

  // Load form from job whenever the dialog opens.
  useEffect(() => {
    if (!open || !job) return;
    setForm({
      title: job.title || "",
      description: job.description || "",
      location: job.location || "Remote",
      job_type: job.job_type || "Full-time",
      experience_min: String(job.experience_min ?? 0),
      experience_max: String(job.experience_max ?? 8),
      required_skills: [...(job.required_skills || [])],
      nice_to_have: [...(job.nice_to_have || [])],
      salary_currency: job.salary_currency || "INR",
      salary_min: job.salary_min == null ? "" : String(job.salary_min),
      salary_max: job.salary_max == null ? "" : String(job.salary_max),
      salary_period: job.salary_period || "yearly",
      salary_visible: job.salary_visible !== false,
    });
    setReqInput("");
    setNiceInput("");
  }, [open, job]);

  // Days remaining in the 7-day edit window
  const daysLeft = useMemo(() => {
    if (!job?.created_at) return 7;
    const created = new Date(job.created_at);
    if (Number.isNaN(created.getTime())) return 7;
    const now = new Date();
    const days = 7 - Math.floor((now - created) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  }, [job]);

  if (!form) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" />
      </Dialog>
    );
  }

  const addSkill = (list, item, resetInput) => {
    const v = String(item || "").trim();
    if (!v || form[list].includes(v)) return;
    setForm((f) => ({ ...f, [list]: [...f[list], v] }));
    resetInput("");
  };
  const removeSkill = (list, v) =>
    setForm((f) => ({ ...f, [list]: f[list].filter((x) => x !== v) }));

  const submit = async () => {
    if (!form.title || !form.description) {
      toast.error("Title and description are required");
      return;
    }
    const expMin = parseInt(form.experience_min || "0", 10);
    const expMax = parseInt(form.experience_max || "0", 10);
    if (expMax < expMin) {
      toast.error("Max experience must be greater than or equal to min");
      return;
    }
    const salMin = form.salary_min === "" ? null : parseInt(form.salary_min, 10);
    const salMax = form.salary_max === "" ? null : parseInt(form.salary_max, 10);
    if (salMin !== null && salMax !== null && salMax < salMin) {
      toast.error("Max salary must be greater than or equal to min salary");
      return;
    }
    setSaving(true);
    try {
      const symbol = CURRENCIES.find((c) => c.code === form.salary_currency)?.symbol || form.salary_currency;
      const salary_range = salMin !== null && salMax !== null
        ? `${symbol}${salMin.toLocaleString()} – ${symbol}${salMax.toLocaleString()} / ${form.salary_period === "monthly" ? "month" : "year"}`
        : "";
      const payload = {
        ...form,
        experience_min: expMin,
        experience_max: expMax,
        salary_min: salMin,
        salary_max: salMax,
        salary_range,
      };
      const { data } = await api.put(`/jobs/${job.job_id}`, payload);
      toast.success("Job updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Edit job</DialogTitle>
          <DialogDescription>
            You have <span className="tag-mono font-semibold text-[#002FA7]">{daysLeft} day{daysLeft === 1 ? "" : "s"}</span> left in the 7-day edit window. After that the posting becomes read-only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Job title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="edit-job-title" className="mt-1.5" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={5} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="edit-job-desc" className="mt-1.5" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} data-testid="edit-job-location" className="mt-1.5" />
            </div>
            <div>
              <Label>Job type</Label>
              <select
                value={form.job_type}
                onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))}
                data-testid="edit-job-type"
                className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
              >
                <option>Full-time</option>
                <option>Contract</option>
                <option>Part-time</option>
                <option>Freelance</option>
              </select>
            </div>
            <div>
              <Label>Min experience (yrs)</Label>
              <Input
                type="number" min="0" max="60" inputMode="numeric"
                value={form.experience_min}
                onChange={(e) => setForm((f) => ({ ...f, experience_min: toNonNegInt(e.target.value) }))}
                onKeyDown={(e) => { if (["-", "e", "E", "+", "."].includes(e.key)) e.preventDefault(); }}
                data-testid="edit-job-exp-min"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Max experience (yrs)</Label>
              <Input
                type="number" min="0" max="60" inputMode="numeric"
                value={form.experience_max}
                onChange={(e) => setForm((f) => ({ ...f, experience_max: toNonNegInt(e.target.value) }))}
                onKeyDown={(e) => { if (["-", "e", "E", "+", "."].includes(e.key)) e.preventDefault(); }}
                data-testid="edit-job-exp-max"
                className="mt-1.5"
              />
            </div>
          </div>

          {/* Salary block */}
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50/40 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-sm font-semibold">Salary</Label>
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-salary-visible"
                  checked={form.salary_visible}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, salary_visible: v }))}
                  data-testid="edit-salary-visible-toggle"
                />
                <Label htmlFor="edit-salary-visible" className="text-xs cursor-pointer">
                  {form.salary_visible ? "Visible" : "Hidden"}
                </Label>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <select
                value={form.salary_currency}
                onChange={(e) => setForm((f) => ({ ...f, salary_currency: e.target.value }))}
                data-testid="edit-salary-currency"
                className="h-10 rounded-md border border-zinc-200 bg-white px-2 text-sm"
              >
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
              </select>
              <Input
                type="number" min="0" placeholder="Min"
                value={form.salary_min}
                onChange={(e) => setForm((f) => ({ ...f, salary_min: toNonNegInt(e.target.value) }))}
                data-testid="edit-salary-min"
              />
              <Input
                type="number" min="0" placeholder="Max"
                value={form.salary_max}
                onChange={(e) => setForm((f) => ({ ...f, salary_max: toNonNegInt(e.target.value) }))}
                data-testid="edit-salary-max"
              />
              <select
                value={form.salary_period}
                onChange={(e) => setForm((f) => ({ ...f, salary_period: e.target.value }))}
                data-testid="edit-salary-period"
                className="h-10 rounded-md border border-zinc-200 bg-white px-2 text-sm"
              >
                <option value="yearly">per year</option>
                <option value="monthly">per month</option>
              </select>
            </div>
          </div>

          <SkillEditor
            label="Required skills"
            list={form.required_skills}
            input={reqInput} setInput={setReqInput}
            add={(v) => addSkill("required_skills", v ?? reqInput, setReqInput)}
            remove={(s) => removeSkill("required_skills", s)}
            testId="edit-req"
          />
          <SkillEditor
            label="Nice to have"
            list={form.nice_to_have}
            input={niceInput} setInput={setNiceInput}
            add={(v) => addSkill("nice_to_have", v ?? niceInput, setNiceInput)}
            remove={(s) => removeSkill("nice_to_have", s)}
            testId="edit-nice"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-job-save" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SkillEditor({ label, list, input, setInput, add, remove, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Type a skill and press enter…"
          data-testid={`${testId}-input`}
        />
        <Button type="button" onClick={() => add()} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid={`${testId}-add-btn`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {list.map((s) => (
          <span key={s} className="tag-mono inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded">
            {s}
            <button type="button" onClick={() => remove(s)} className="text-blue-600 hover:text-blue-900" data-testid={`${testId}-remove-${s}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {COMMON_SKILLS.filter((s) => !list.includes(s)).slice(0, 10).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => add(s)}
            data-testid={`${testId}-suggest-${s}`}
            className="tag-mono inline-flex items-center gap-1 px-2 py-1 bg-white border border-zinc-200 hover:border-[#002FA7] hover:text-[#002FA7] text-zinc-700 rounded transition-colors text-xs"
          >
            <Plus className="h-3 w-3" /> {s}
          </button>
        ))}
      </div>
    </div>
  );
}
